import {
    AfterViewInit,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { DataTableDirective } from 'angular-datatables';
import { Subject } from 'rxjs';
import { ActivityFiltersPayload } from 'src/app/components/shared/activity-filters/activity-filters.interface';
import {
    NedistribuiteDocumentRow,
    NedistribuiteDrillPayload,
    NedistribuiteDrillResponse,
    NedistribuiteResponse,
    NedistribuiteTipRow,
    NedistribuiteUserRow,
    RegistruEntry,
} from '../../raport-activitate-detaliat.interface';
import { RaportActivitateDetaliatService } from '../../raport-activitate-detaliat.service';

@Component({
    selector: 'app-matrix-nedistribuite',
    templateUrl: './matrix-nedistribuite.component.html',
    styleUrls: ['./matrix-nedistribuite.component.scss'],
})
export class MatrixNedistribuiteComponent implements AfterViewInit, OnChanges, OnDestroy {
    @Input() summary: NedistribuiteResponse | null = null;
    @Input() filters!: ActivityFiltersPayload;
    @Input() canViewDocument = false;
    /** mod status: open (implicit) | closed | all — controlat de aici, aplicat de părinte pe re-fetch */
    @Input() statusMode: 'open' | 'closed' | 'all' = 'open';

    @Output() openDocument = new EventEmitter<NedistribuiteDocumentRow>();
    @Output() statusModeChange = new EventEmitter<'open' | 'closed' | 'all'>();

    @ViewChild(DataTableDirective, { static: false }) dtElement?: DataTableDirective;

    dtOptions: DataTables.Settings = {};
    dtTrigger: Subject<DataTables.Settings> = new Subject();

    isLoading = false;

    // filtre la nivel de sub-rows
    // selecția pe creator e tratată individual pe combinația user > compartiment > direcție
    selectedUser: NedistribuiteUserRow | null = null;
    selectedTipId: number | null = null;

    // toggle pentru lista „per tip document"
    tipFilterActive = false;

    private dtInitialized = false;
    private lastFiltersHash = '';

    constructor(private service: RaportActivitateDetaliatService) {}

    ngAfterViewInit(): void {
        this.buildDtOptions();
        this.lastFiltersHash = this.computeHash();
        // tabelul e MEREU în DOM → îl inițializăm o singură dată aici (fix „nu se încarcă");
        // restul e doar ajax.reload(). setTimeout ca directiva datatable să fie abonată la dtTrigger.
        setTimeout(() => {
            this.dtTrigger.next(this.dtOptions);
            this.dtInitialized = true;
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['summary']) {
            // summary nou (căutare nouă) → aplică scope-ul automat (preselect/preload la user)
            this.applyAutoScope();
            return;
        }
        if (changes['filters'] && this.dtInitialized) {
            const h = this.computeHash();
            if (h !== this.lastFiltersHash) {
                this.lastFiltersHash = h;
                this.reloadTable();
            }
        }
    }

    /**
     * La un summary nou: resetează sub-filtrele; dacă la DESTINAȚIE e selectat un singur
     * utilizator și există documente nedistribuite, îl preselectează și preîncarcă lista
     * (fără filtru de tip activ). Altfel, dacă drill-ul era deja deschis, îl reîncarcă pe noul scope.
     */
    private applyAutoScope(): void {
        this.selectedTipId = null;
        this.tipFilterActive = false;

        const destUser = this.filters?.destinatie?.user_id ?? null;
        const hasDocs = (this.summary?.total ?? 0) > 0;
        const perUser = this.summary?.per_user ?? [];

        // preselectăm creatorul doar dacă e o singură combinație (user selectat la destinație);
        // altfel lăsăm scope-ul pe destinație (aplicat de BE)
        this.selectedUser = destUser !== null && hasDocs && perUser.length === 1 ? perUser[0] : null;

        this.lastFiltersHash = this.computeHash();
        this.reloadTable();
    }

    ngOnDestroy(): void {
        this.dtTrigger.complete();
    }

    keys(): { total: number } {
        return { total: this.summary?.total ?? 0 };
    }

    onSelectUser(row: NedistribuiteUserRow): void {
        this.selectedUser = this.isUserSelected(row) ? null : row;
        this.reloadTable();
    }

    /** Selecție/evidențiere pe combinația exactă user > compartiment > direcție. */
    isUserSelected(row: NedistribuiteUserRow): boolean {
        const s = this.selectedUser;
        return !!s
            && s.id_user === row.id_user
            && s.id_compartiment === row.id_compartiment
            && s.id_directie === row.id_directie;
    }

    onSelectTip(row: NedistribuiteTipRow): void {
        this.selectedTipId = this.selectedTipId === row.id ? null : row.id;
        this.reloadTable();
    }

    /** Comutator pentru lista „per tip document". La oprire, resetează filtrul de tip. */
    toggleTipFilter(): void {
        this.tipFilterActive = !this.tipFilterActive;
        if (!this.tipFilterActive && this.selectedTipId !== null) {
            this.selectedTipId = null;
            this.reloadTable();
        }
    }

    /** Ia toate tipurile de documente (curăță selecția de tip), păstrând filtrul de creator. */
    selectAllTipuri(): void {
        this.selectedTipId = null;
        this.reloadTable();
    }

    clearSubFilters(): void {
        this.selectedUser = null;
        this.selectedTipId = null;
        this.reloadTable();
    }

    /** Forțează reîncărcarea listei de documente nedistribuite. */
    afiseazaLista(): void {
        this.reloadTable();
    }

    /** Schimbă modul de status (open/closed/all) — părintele re-încarcă summary-ul; drill-ul se reîncarcă la sosirea noului summary. */
    setStatusMode(mode: 'open' | 'closed' | 'all'): void {
        if (this.statusMode === mode) {
            return;
        }
        this.statusModeChange.emit(mode);
    }

    private computeHash(): string {
        const s = this.selectedUser;
        const u = s ? `${s.id_user}:${s.id_directie}:${s.id_compartiment}` : '';
        const suffix = `|u:${u}|t:${this.selectedTipId ?? ''}`;
        try {
            return JSON.stringify(this.filters) + suffix;
        } catch {
            return suffix;
        }
    }

    private reloadTable(): void {
        const p = this.dtElement?.dtInstance;
        if (!p || typeof (p as any).then !== 'function') {
            return;
        }
        (p as Promise<DataTables.Api>).then((dt) => dt.ajax.reload());
    }

    private buildDtOptions(): void {
        this.dtOptions = {
            pagingType: 'full_numbers',
            pageLength: 25,
            serverSide: true,
            processing: true,
            searching: false,
            ordering: true,
            order: [[1, 'desc']],
            language: {
                emptyTable: 'Niciun document nedistribuit pentru filtrele selectate.',
                info: 'Afișează _START_ - _END_ din _TOTAL_ documente',
                infoEmpty: '0 documente',
                lengthMenu: 'Afișează _MENU_ documente per pagină',
                loadingRecords: 'Se încarcă...',
                processing: 'Se procesează...',
                zeroRecords: 'Niciun document găsit.',
                paginate: { first: '«', last: '»', next: '›', previous: '‹' },
            },
            ajax: (params: any, callback) => {
                this.isLoading = true;
                const orderRaw = params.order ?? [];
                const colsRaw = params.columns ?? [];
                const order = orderRaw.map((o: any) => ({
                    column: this.columnIndexToName(o.column, colsRaw),
                    dir: o.dir as 'asc' | 'desc',
                }));

                const payload: NedistribuiteDrillPayload = {
                    origin: {
                        ...this.filters.origin,
                        // sub-filtru creator, tratat pe combinația user > compartiment > direcție
                        user_id: this.selectedUser?.id_user ?? null,
                        compartiment_id: this.selectedUser?.id_compartiment ?? null,
                        directie_id: this.selectedUser?.id_directie ?? null,
                    },
                    destinatie: this.filters.destinatie,
                    info: {
                        ...this.filters.info,
                        // filtrul de tip la nivel de sub-rows
                        tip_document_id: this.selectedTipId ?? this.filters.info.tip_document_id ?? null,
                        // modul de status (open/closed/all) — sincron cu summary-ul
                        nedistribuite_status_mode: this.statusMode,
                    },
                    pagination: {
                        start: params.start ?? 0,
                        length: params.length ?? 25,
                        order,
                    },
                    draw: params.draw ?? 1,
                };

                this.service.getNedistribuiteDrillDown(payload).subscribe({
                    next: (resp: NedistribuiteDrillResponse) => {
                        this.isLoading = false;
                        callback({
                            recordsTotal: resp.recordsTotal,
                            recordsFiltered: resp.recordsFiltered,
                            data: resp.data,
                            draw: resp.draw,
                        });
                    },
                    error: (err) => {
                        this.isLoading = false;
                        console.error('Error loading nedistribuite drill-down:', err);
                        callback({ recordsTotal: 0, recordsFiltered: 0, data: [], draw: params.draw });
                    },
                });
            },
            columns: [
                {
                    data: null,
                    orderable: true,
                    name: 'reg_number',
                    render: (_d: any, _t: any, row: NedistribuiteDocumentRow) => this.renderRegistruCell(row),
                },
                { data: 'reg_date', orderable: true, name: 'reg_date' },
                {
                    data: 'creator_user_name',
                    orderable: false,
                    render: (d: any, _t: any, row: NedistribuiteDocumentRow) =>
                        d ? this.escapeHtml(String(d)) : (row.creator_user_id ? '#' + row.creator_user_id : '<span class="text-muted small">—</span>'),
                },
                { data: 'categorie_name', orderable: false, name: 'id_categorie' },
                { data: 'tip_document_name', orderable: false, name: 'id_tip_document' },
                { data: 'current_status_name', orderable: false, name: 'current_status' },
                {
                    data: 'source_name',
                    orderable: false,
                    render: (d: any) => (d ? this.escapeHtml(String(d)) : '<span class="text-muted small">—</span>'),
                },
                { data: 'observatii', orderable: false },
                { data: null, orderable: false },
            ],
            columnDefs: [
                {
                    targets: -1,
                    render: () => {
                        if (!this.canViewDocument) {
                            return '<span class="text-muted small">—</span>';
                        }
                        return '<button class="btn btn-sm btn-info open-doc-btn" title="Vizualizare document">' +
                            '<i class="fa fa-eye"></i></button>';
                    },
                },
            ],
            rowCallback: (row: Node, data: object | any[]) => {
                if (!this.canViewDocument) {
                    (row as HTMLElement).style.cursor = 'default';
                    return;
                }
                (row as HTMLElement).style.cursor = 'pointer';
                row.addEventListener('click', () => this.openDocument.emit(data as NedistribuiteDocumentRow));
            },
        };
    }

    private columnIndexToName(idx: number, cols: any[]): string {
        const fallback = 'reg_date';
        if (!cols || !cols[idx]) return fallback;
        return cols[idx].name || cols[idx].data || fallback;
    }

    private renderRegistruCell(row: NedistribuiteDocumentRow): string {
        const entries = row.registru_entries ?? [];
        if (entries.length === 0) {
            const number = row.reg_number !== null && row.reg_number !== undefined ? String(row.reg_number) : '—';
            return `<div class="registru-line">${this.formatRegistruInline(null, number)}</div>`;
        }
        return entries
            .map((e: RegistruEntry) => {
                const number = e.reg_number !== null && e.reg_number !== undefined ? String(e.reg_number) : '—';
                const dateLabel = e.reg_date ? this.escapeHtml(e.reg_date.substring(0, 10)) : '';
                const datePart = dateLabel ? `<span class="registru-date">${dateLabel}</span>` : '';
                return `<div class="registru-line">${this.formatRegistruInline(e.serie_prefix, number)}${datePart}</div>`;
            })
            .join('');
    }

    private formatRegistruInline(prefixRaw: string | null, number: string): string {
        const numberSafe = this.escapeHtml(number);
        if (!prefixRaw) {
            return `<span class="registru-text"><span class="registru-number">${numberSafe}</span></span>`;
        }
        const prefix = prefixRaw.replace(/\.+$/, '').trim();
        return `<span class="registru-text"><span class="registru-prefix">${this.escapeHtml(prefix)}.</span> <span class="registru-number">${numberSafe}</span></span>`;
    }

    private escapeHtml(s: string): string {
        return s.replace(/[&<>"']/g, (ch) => {
            switch (ch) {
                case '&': return '&amp;';
                case '<': return '&lt;';
                case '>': return '&gt;';
                case '"': return '&quot;';
                case "'": return '&#39;';
            }
            return ch;
        });
    }
}
